<template>
  <Teleport to="body">
    <div class="toast-container" aria-live="polite" aria-atomic="true">
      <TransitionGroup name="toast" tag="div" class="toast-list">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast-item', toast.bgClass]"
          role="alert"
          aria-live="assertive"
        >
          <!-- Icon -->
          <div class="toast-icon">
            <component :is="getIconComponent(toast.icon)" />
          </div>
          
          <!-- Content - Only message -->
          <span class="toast-message">{{ toast.message || toast.title }}</span>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script>
import { defineComponent, h } from 'vue'

// SVG Icons as functional components - using viewBox with padding for stroke
const icons = {
  'check-circle': () => h('svg', { 
    width: 18, height: 18, viewBox: '-1 -1 26 26', fill: 'none', 
    stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    style: 'display: block;'
  }, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('polyline', { points: '9 12 11 14 15 10' })
  ]),
  'x-circle': () => h('svg', { 
    width: 18, height: 18, viewBox: '-1 -1 26 26', fill: 'none', 
    stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    style: 'display: block;'
  }, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('line', { x1: 15, y1: 9, x2: 9, y2: 15 }),
    h('line', { x1: 9, y1: 9, x2: 15, y2: 15 })
  ]),
  'alert-triangle': () => h('svg', { 
    width: 18, height: 18, viewBox: '-1 -1 26 26', fill: 'none', 
    stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    style: 'display: block;'
  }, [
    h('path', { d: 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }),
    h('line', { x1: 12, y1: 9, x2: 12, y2: 13 }),
    h('line', { x1: 12, y1: 17, x2: 12.01, y2: 17 })
  ]),
  'info': () => h('svg', { 
    width: 18, height: 18, viewBox: '-1 -1 26 26', fill: 'none', 
    stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round',
    style: 'display: block;'
  }, [
    h('circle', { cx: 12, cy: 12, r: 10 }),
    h('line', { x1: 12, y1: 16, x2: 12, y2: 12 }),
    h('line', { x1: 12, y1: 8, x2: 12.01, y2: 8 })
  ])
}

export default defineComponent({
  name: 'ToastContainer',
  props: {
    toasts: {
      type: Array,
      required: true
    }
  },
  setup() {
    const getIconComponent = (iconName) => {
      return icons[iconName] || icons['info']
    }
    
    return {
      getIconComponent
    }
  }
})
</script>

<style lang="scss">
// Toast Container - Centered at top
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.toast-list {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.toast-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-radius: 50px;
  pointer-events: auto;
  position: relative;
  white-space: nowrap;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
  }
}

// Icon styles
.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
}

// Message
.toast-message {
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
  color: inherit;
}

// ============================================
// Toast Type Variants with Gradients & Glow
// ============================================

// Success Toast
.toast-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  box-shadow: 
    0 4px 12px rgba(16, 185, 129, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

// Error Toast
.toast-error {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  color: #fff;
  box-shadow: 
    0 4px 12px rgba(239, 68, 68, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

// Warning Toast
.toast-warning {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #fff;
  box-shadow: 
    0 4px 12px rgba(245, 158, 11, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

// Info Toast
.toast-info {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: #fff;
  box-shadow: 
    0 4px 12px rgba(59, 130, 246, 0.4),
    0 2px 4px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

// ============================================
// Animations
// ============================================

.toast-enter-active {
  animation: toast-in 0.35s cubic-bezier(0.21, 1.02, 0.73, 1) forwards;
}

.toast-leave-active {
  animation: toast-out 0.25s cubic-bezier(0.06, 0.71, 0.55, 1) forwards;
}

.toast-move {
  transition: transform 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
}

@keyframes toast-in {
  0% {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes toast-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(-15px) scale(0.95);
  }
}

// ============================================
// Dark Mode Support
// ============================================

body.dark-mode {
  .toast-success {
    box-shadow: 
      0 4px 16px rgba(16, 185, 129, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  .toast-error {
    box-shadow: 
      0 4px 16px rgba(239, 68, 68, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  .toast-warning {
    box-shadow: 
      0 4px 16px rgba(245, 158, 11, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  .toast-info {
    box-shadow: 
      0 4px 16px rgba(59, 130, 246, 0.5),
      0 2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
}
</style>
